import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import { db } from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaGlobe, FaFacebook, FaInstagram, FaWhatsapp, FaGoogle, FaLinkedin, FaTwitter, FaYoutube, FaGithub, FaStar } from 'react-icons/fa';

const { FiLoader, FiAlertOctagon, FiExternalLink, FiArrowRight, FiCheck } = FiIcons;

// Helper to get icon based on URL or label
const getLinkIcon = (url, label) => {
  const lower = (url + label).toLowerCase();
  if (lower.includes('facebook')) return FaFacebook;
  if (lower.includes('instagram')) return FaInstagram;
  if (lower.includes('whatsapp') || lower.includes('wa.me')) return FaWhatsapp;
  if (lower.includes('google')) return FaGoogle;
  if (lower.includes('linkedin')) return FaLinkedin;
  if (lower.includes('twitter') || lower.includes('x.com')) return FaTwitter;
  if (lower.includes('youtube')) return FaYoutube;
  if (lower.includes('github')) return FaGithub;
  return FaGlobe;
};

const TagRouter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // State for different render modes
  const [directDest, setDirectDest] = useState(null);
  const [profileData, setProfileData] = useState(null);
  
  // Review Filter State
  const [reviewMode, setReviewMode] = useState(null); // 'rating', 'feedback', 'redirecting', 'thanks'
  const [rating, setRating] = useState(0);
  const [reviewConfig, setReviewConfig] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);

  useEffect(() => {
    const handleRoute = async () => {
      const tagCode = searchParams.get('tag_id');
      if (!tagCode) {
        setError("Invalid card: Missing Tag ID.");
        return;
      }

      try {
        let record = await db.findByTagId(tagCode);

        // If record doesn't exist, create it dynamically
        if (!record) {
          record = await db.create(tagCode);
          if (record) {
            navigate('/setup', { state: { ...record } });
          } else {
            setError("Could not register this tag. Please check database connection.");
          }
          return;
        }

        if (record.is_active !== 1) {
          navigate('/setup', { state: { ...record } });
          return;
        }
        
        setCurrentTag(record);
        db.incrementScan(record);

        // CHECK NEGATIVE REVIEW FILTER
        if (record.negative_review_filter_enabled === 1 && record.google_review_link) {
            setReviewConfig({
                googleLink: record.google_review_link,
                dummyMessage: record.dummy_page_message || "Thank you for your honest feedback. We'll work on improving!"
            });
            setReviewMode('rating');
            return;
        }

        // CHECK LINK TYPE
        if (record.link_type === 'smart') {
          // Fetch Profile Links
          const links = await db.getProfileLinks(tagCode);
          setProfileData({
            company: record.company_name,
            logoUrl: record.logo_url,
            bio: record.bio,
            links: links
          });
          return;
        }

        // DIRECT LINK LOGIC
        let finalUrl = record.redirect_url || '';
        if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
          finalUrl = `https://${finalUrl}`;
        }

        // Auto-redirect logic
        const isIframe = window.self !== window.top;
        if (!isIframe) {
          window.location.replace(finalUrl);
        } else {
          setDirectDest(finalUrl);
        }

      } catch (err) {
        console.error("Router error:", err);
        setError("Network error. Please check connection.");
      }
    };

    handleRoute();
  }, [searchParams, navigate]);

  // Handle Rating Selection
  const handleRating = (stars) => {
    setRating(stars);
    
    if (stars >= 4) {
        setReviewMode('redirecting');
        setTimeout(() => {
            window.location.replace(reviewConfig.googleLink);
        }, 1500);
    } else {
        setReviewMode('feedback');
    }
  };

  // Handle Negative Feedback Submission
  const submitFeedback = async () => {
    if (!feedback.trim()) return;
    setIsSubmitting(true);
    
    try {
        await db.createNegativeReview({
            tag_code: currentTag.tag_code,
            rating: rating,
            feedback: feedback,
            user_name: userName,
            user_email: '' // Optional, not collecting for now to keep it simple
        });
        setReviewMode('thanks');
    } catch (err) {
        console.error("Feedback submit error", err);
        alert("Something went wrong. Please try again.");
    } finally {
        setIsSubmitting(false);
    }
  };

  // --- RENDER: ERROR ---
  if (error) {
    return (
      <Layout hideActions={true}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4 px-4">
          <SafeIcon icon={FiAlertOctagon} className="text-3xl text-red-500" />
          <h2 className="text-2xl font-bold">Something went wrong</h2>
          <p className="text-zinc-400">{error}</p>
        </div>
      </Layout>
    );
  }

  // --- RENDER: REVIEW FILTER FLOW ---
  if (reviewMode) {
    return (
        <div className="min-h-screen bg-white text-zinc-900 font-sans flex flex-col items-center justify-center p-6">
            <div className="w-full max-w-md space-y-8 text-center animate-in fade-in slide-in-from-bottom-8 duration-500">
                
                {/* Logo / Brand Placeholder */}
                <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20 transform -rotate-3">
                    <SafeIcon icon={FaStar} className="text-3xl text-white" />
                </div>

                {reviewMode === 'rating' && (
                    <div className="space-y-8">
                        <div>
                            <h1 className="text-2xl font-bold mb-2">How was your experience?</h1>
                            <p className="text-zinc-500">Tap a star to rate us</p>
                        </div>
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    onClick={() => handleRating(star)}
                                    className="text-4xl transition-transform hover:scale-110 active:scale-95 focus:outline-none"
                                >
                                    <SafeIcon 
                                        icon={FaStar} 
                                        className={star <= rating ? "text-amber-400" : "text-zinc-200"} 
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {reviewMode === 'redirecting' && (
                    <div className="space-y-6">
                        <div className="w-16 h-16 border-4 border-amber-100 border-t-amber-500 rounded-full animate-spin mx-auto" />
                        <div>
                            <h2 className="text-xl font-bold">Thank you!</h2>
                            <p className="text-zinc-500">Redirecting to Google Reviews...</p>
                        </div>
                        <button 
                            onClick={() => window.location.replace(reviewConfig.googleLink)}
                            className="text-sm text-amber-600 underline"
                        >
                            Click here if not redirected
                        </button>
                    </div>
                )}

                {reviewMode === 'feedback' && (
                    <div className="space-y-6 text-left">
                        <div className="text-center">
                            <h2 className="text-xl font-bold">We're sorry to hear that</h2>
                            <p className="text-zinc-500 text-sm">Your feedback helps us improve.</p>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">Name (Optional)</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                                    placeholder="Your Name"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-zinc-400 uppercase tracking-wider mb-1">How can we improve?</label>
                                <textarea 
                                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50 min-h-[120px]"
                                    placeholder="Tell us what went wrong..."
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <Button 
                                onClick={submitFeedback} 
                                disabled={!feedback.trim() || isSubmitting}
                                className="bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20"
                            >
                                {isSubmitting ? "Sending..." : "Submit Feedback"}
                            </Button>
                        </div>
                    </div>
                )}

                {reviewMode === 'thanks' && (
                    <div className="space-y-6">
                         <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <SafeIcon icon={FiCheck} className="text-4xl text-green-500" />
                         </div>
                        <div>
                            <h2 className="text-xl font-bold">Feedback Received</h2>
                            <p className="text-zinc-500 mt-2">{reviewConfig.dummyMessage}</p>
                        </div>
                    </div>
                )}

                <div className="fixed bottom-6 left-0 right-0 text-center">
                    <p className="text-zinc-300 text-xs">Powered by ConnectKard</p>
                </div>
            </div>
        </div>
    );
  }

  // --- RENDER: SMART PROFILE ---
  if (profileData) {
    return (
      <Layout hideActions={true}>
        <div className="min-h-[70vh] flex flex-col items-center pt-8 animate-in fade-in slide-in-from-bottom-8 duration-500">
          
          {/* Header */}
          <div className="text-center space-y-4 mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-zinc-800 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-red-900/30 border-4 border-black overflow-hidden relative">
              {profileData.logoUrl ? (
                <img src={profileData.logoUrl} alt="Logo" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
              ) : (
                <span className="text-3xl font-bold text-white uppercase">
                  {profileData.company?.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{profileData.company}</h1>
              {profileData.bio && <p className="text-zinc-400 text-sm mt-1 max-w-xs mx-auto">{profileData.bio}</p>}
            </div>
          </div>

          {/* Links List */}
          <div className="w-full max-w-sm space-y-4 px-4 pb-12">
            {profileData.links.map((link, i) => {
              const Icon = getLinkIcon(link.url, link.label);
              return (
                <a 
                  key={link.id || i}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-red-500/50 transition-all p-4 rounded-xl flex items-center gap-4 group"
                >
                  <div className="w-10 h-10 bg-zinc-950 rounded-full flex items-center justify-center text-zinc-400 group-hover:text-red-500 transition-colors">
                    <Icon size={20} />
                  </div>
                  <span className="font-semibold text-zinc-200 group-hover:text-white flex-1 text-center pr-10">
                    {link.label}
                  </span>
                </a>
              );
            })}
          </div>

          <div className="mt-auto pb-8 text-zinc-600 text-xs">
            Powered by ConnectKard
          </div>
        </div>
      </Layout>
    );
  }

  // --- RENDER: DIRECT LINK PREVIEW (IFRAME/DEV) ---
  if (directDest) {
    return (
      <Layout hideActions={true}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="space-y-4">
            <div className="w-20 h-20 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-red-900/10">
              <SafeIcon icon={FiExternalLink} className="text-4xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Opening Link...</h2>
              <p className="text-zinc-400 max-w-xs mx-auto mt-2">
                Destination: <span className="text-white font-medium">{new URL(directDest).hostname}</span>
              </p>
            </div>
          </div>
          <div className="w-full max-w-xs space-y-4">
            <Button onClick={() => window.open(directDest, '_blank')} className="w-full py-4 text-lg shadow-red-500/20">
              Open Site <SafeIcon icon={FiArrowRight} />
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // --- RENDER: LOADING ---
  return (
    <Layout hideActions={true}>
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 border-t-red-600 rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <SafeIcon icon={FiLoader} className="text-zinc-600 animate-pulse" />
          </div>
        </div>
        <p className="text-zinc-400 animate-pulse">Connecting you to infinite possibilities...</p>
      </div>
    </Layout>
  );
};

export default TagRouter;